export const Router = {
    go: (path) => {
        window.history.pushState(null, "", path);
        window.dispatchEvent(new globalThis.Event("popstate"));
    }
};
import { Event, Storage } from "../models/enums";
import Utils from "../utils/utils";
import { Backend, ResponseError } from "./backend.service";
import { EventBus } from "./eventbus.service";
export class DataStore {
    constructor() {
        this._version = "dev";
        this._online = true;
        this._logs = [];
        this._webpushPublicKey = 'BD7e8epUS7uVHmhP5oJkVQS6Zji0KPdDbduSry2DrUFxJ-yAqMsvomgVkLVWaBchqZNyMtQoqxZ6XpNwUDNAW7o';
        const versionMeta = document.querySelector('meta[name="app-version"]');
        if (versionMeta) {
            const version = versionMeta.getAttribute('content');
            if (version && version !== '{v}') {
                this._version = version;
            }
        }
        this.backend = Backend.getInstance();
        this.eventbus = EventBus.getInstance();
        if (localStorage.getItem(Storage.User)) {
            this._user = JSON.parse(localStorage.getItem(Storage.User) || "{}");
            this.addLog(`user data loaded for ${this._user?.email}`);
        }
        if (localStorage.getItem(Storage.Token)) {
            try {
                this._token = JSON.parse(localStorage.getItem(Storage.Token) || "{}");
                this.addLog(`token data loaded for ${this._user?.email}`);
            }
            catch (error) {
                console.error("failed at reading stored token");
            }
        }
        if (localStorage.getItem(Storage.Page)) {
            this._currentPage = JSON.parse(localStorage.getItem(Storage.Page) || "{}");
            this.addLog(`current page loaded and set to ${this._currentPage}`);
        }
        this.validRefreshToken();
    }
    static getInstance() {
        if (!DataStore.instance) {
            DataStore.instance = new DataStore();
            DataStore.instance._online = navigator.onLine;
            DataStore.instance.addLog(`online status set to ${DataStore.instance._online}`);
        }
        return DataStore.instance;
    }
    addLog(log) {
        if (this._logs.length >= 25) {
            this._logs.shift();
        }
        this._logs.push(Utils.utcDateString() + ": " + log);
        console.log(log);
    }
    reload() {
        this.addLog(`reload to current ${this._currentPage}`);
        window.location.reload();
    }
    redirect(page) {
        if (page !== undefined) {
            if (page === "/" && this._currentPage) {
                this.addLog(`redirect from / to ${this._currentPage}`);
                Router.go(this._currentPage);
            }
            else {
                this.addLog(`redirect directly to ${page}`);
                Router.go(page);
            }
        }
        else {
            if (this._currentPage) {
                this.addLog(`redirect to current ${this._currentPage}`);
                Router.go(this._currentPage);
            }
            else {
                this.addLog(`redirect to default ${this._currentPage}`);
                Router.go("/cards/current");
            }
        }
    }
    verifyPageAccess(page) {
        this.currentPage = page;
        if (this._user === undefined || this._token === undefined || !this.validRefreshToken()) {
            this.addLog(`access not granted - redirecting to login`);
            Router.go("/login");
        }
    }
    validRefreshToken() {
        try {
            if (this._token !== undefined) {
                const now = new Date();
                const refreshToken = Utils.parseJwt(this._token.refresh_token);
                const refreshDiff = (Utils.numberToDate(refreshToken.exp).getTime() - now.getTime()) / 1000;
                if (refreshDiff < 0) {
                    this.addLog(`token expired`);
                    return false;
                }
                if (refreshDiff < 60 * 60 * 24 * 15) {
                    this.addLog("less than 15 days left - token being refreshed");
                    this.backend
                        .oidcRefresh(this.token.refresh_token)
                        .then((response) => {
                        this.token = response;
                    })
                        .catch((error) => {
                        this.eventbus.dispatchError(error);
                    });
                }
                return true;
            }
            else {
                this.addLog(`no token available`);
                return false;
            }
        }
        catch (error) {
            return false;
        }
    }
    async validAccessToken() {
        if (this._token) {
            const now = new Date();
            const accessToken = Utils.parseJwt(this._token.access_token);
            const accessDiff = (Utils.numberToDate(accessToken.exp).getTime() - now.getTime()) / 1000;
            if (accessDiff < 5) {
                this.addLog(`access token less than 5 sec left - refreshing`);
                await this.backend
                    .oidcRefresh(this.token.refresh_token)
                    .then((response) => {
                    this.token = response;
                })
                    .catch((error) => {
                    this.eventbus.dispatchError(error);
                });
            }
        }
    }
    async fetchLink(id, gid) {
        await this.validAccessToken();
        return this.backend.getLink(this.token?.access_token, id, gid);
    }
    async acceptOrRejectTheCard(id, gid, accept) {
        await this.validAccessToken();
        const access_token = this.token?.access_token;
        return this.backend.acceptOrRejectTheCard(access_token, id, gid, accept);
    }
    async clipTheCard(id, gid, message) {
        await this.validAccessToken();
        const access_token = this.token?.access_token;
        return this.backend.clipTheCard(access_token, id, gid, message);
    }
    async clipTheCardDelivered(id, gid, clip) {
        await this.validAccessToken();
        const access_token = this.token?.access_token;
        return this.backend.clipTheCardDelivered(access_token, id, gid, clip);
    }
    async clipTheCardReceived(id, gid, clip) {
        await this.validAccessToken();
        const access_token = this.token?.access_token;
        return this.backend.clipTheCardReceived(access_token, id, gid, clip);
    }
    async registerUser(firstname, lastname, email) {
        return this.backend.registerUser(firstname, lastname, email);
    }
    async verifyUser(id, randomized) {
        return this.backend.verifyUser(id, randomized);
    }
    async resetUser(email) {
        return this.backend.resetUser(email);
    }
    async resetUserVerification(id, randomized) {
        return this.backend.resetUserVerification(id, randomized);
    }
    async changeEmail(id, randomized) {
        return this.backend.changeEmail(id, randomized);
    }
    async fetchOidcLogin(username, password) {
        await this.backend
            .oidcLogin(username, password)
            .then((response) => {
            this.token = response;
        });
        await this.backend.authenticatedUser(this.token.access_token)
            .then((response) => {
            this.user = response;
            this.addLog(`login ${response.email} complete`);
            this.eventbus.dispatchEvent(Event.RequestUpdate);
            this.redirect();
        });
    }
    async fetchAuthenticatedUser() {
        await this.validAccessToken();
        return this.backend.authenticatedUser(this.token.access_token);
    }
    async updateName(nameu) {
        await this.validAccessToken();
        await this.backend.putName(this.token.access_token, nameu);
        return this.fetchAuthenticatedUser()
            .then((response) => {
            this.user = response;
            this.eventbus.dispatchEvent(Event.RequestUpdate);
        })
            .catch((error) => {
            throw new ResponseError(error, JSON.stringify({ "errorcode": error.status, "errortext": error.message }));
        });
    }
    async updateEmail(emailu) {
        await this.validAccessToken();
        return this.backend.putEmail(this.token.access_token, emailu);
    }
    async updatePassword(pwu) {
        await this.validAccessToken();
        return this.backend.putPassword(this.token.access_token, pwu);
    }
    async fetchCardlist(select) {
        await this.validAccessToken();
        return this.backend.fetchCardlist(this.token.access_token, select);
    }
    async fetchContentList() {
        await this.validAccessToken();
        return this.backend.fetchContentlist(this.token.access_token);
    }
    async fetchContentPage(title) {
        return this.backend.fetchPublicContent(title);
    }
    async updateContent(content) {
        await this.validAccessToken();
        return this.backend.putContent(this.token.access_token, content);
    }
    async fetchExistingEmails() {
        await this.validAccessToken();
        return this.backend.getExistingEmails(this.token.access_token);
    }
    async createCard(card) {
        await this.validAccessToken();
        return this.backend.postCard(this.token.access_token, card);
    }
    async updateCard(card) {
        await this.validAccessToken();
        return this.backend.putCard(this.token.access_token, card);
    }
    async updateStatus(card) {
        await this.validAccessToken();
        return this.backend.putStatus(this.token.access_token, card);
    }
    async archiveReceivedCard(card) {
        await this.validAccessToken();
        return this.backend.archiveReceivedCard(this.token.access_token, card);
    }
    async webpushSubscriptions() {
        await this.validAccessToken();
        return this.backend.fetchWebpushsubscriptions(this.token.access_token);
    }
    async webpushSubscriptionPost(encodedKey, encodedAuth, endpoint) {
        await this.validAccessToken();
        const data = { publicKey: encodedKey, auth: encodedAuth, notificationEndPoint: endpoint };
        return this.backend.postWebpushSubscription(this.token.access_token, data);
    }
    async webpushSubscriptionDelete(endpoint) {
        await this.validAccessToken();
        const data = { notificationEndPoint: endpoint };
        return this.backend.postWebpushUnsubscription(this.token.access_token, data);
    }
    async webpushSubscribe() {
        await this.validAccessToken();
        const applicationServerKey = Utils.urlB64ToUint8Array(this._webpushPublicKey);
        const registration = await navigator.serviceWorker.ready;
        const subscribeParams = { userVisibleOnly: true, applicationServerKey: applicationServerKey };
        await registration.pushManager.subscribe(subscribeParams)
            .then(async (subscription) => {
            const endpoint = subscription.endpoint;
            const key = subscription.getKey('p256dh');
            const auth = subscription.getKey('auth');
            const encodedKey = btoa(String.fromCharCode.apply(null, [...new Uint8Array(key)]));
            const encodedAuth = btoa(String.fromCharCode.apply(null, [...new Uint8Array(auth)]));
            await this.webpushSubscriptionPost(encodedKey, encodedAuth, endpoint)
                .then((_) => { });
        })
            .catch((e) => {
            console.error('unable to subscribe to push', e);
        });
    }
    async webpushUnsubscribe() {
        await this.validAccessToken();
        const reg = await navigator.serviceWorker.ready;
        await reg.pushManager.getSubscription()
            .then(async (subscription) => {
            if (subscription) {
                const data = { notificationEndPoint: subscription.endpoint };
                await this.backend.postWebpushUnsubscription(this.token.access_token, data)
                    .then((_) => {
                    subscription.unsubscribe();
                });
            }
            this.eventbus.dispatchEvent(Event.RequestUpdate);
        })
            .catch((e) => {
            console.error('Error unsubscribing', e);
        });
    }
    async webpushSend(data) {
        await this.validAccessToken();
        return this.backend.postWebpushSendMessage(this.token.access_token, data);
    }
    logoutUser() {
        this.addLog(`logout user ${this._user?.email}`);
        this.clearData();
        this.eventbus.dispatchEvent(Event.RequestUpdate);
        Router.go("/");
    }
    createEmptyCard() {
        const card = {};
        card.recipient = "";
        card.created = Utils.utcDateString();
        card.status = "draft";
        card.title = "";
        card.description = "";
        card.size = 3;
        card.timeout = 0;
        card.timeouttype = "seconds";
        card.clips = [];
        return card;
    }
    clearData() {
        localStorage.clear();
        this._logs = [];
        this._currentPage = undefined;
        this._token = undefined;
        this._user = undefined;
    }
    get currentPage() {
        return this._currentPage;
    }
    set currentPage(value) {
        this._currentPage = value;
        localStorage.setItem(Storage.Page, JSON.stringify(this._currentPage));
        this.addLog(`currentpage set to ${this._currentPage}`);
    }
    get token() {
        return this._token;
    }
    set token(value) {
        this._token = value;
        this.saveLocalToken();
        this.addLog(`token saved`);
    }
    get user() {
        return this._user;
    }
    set user(val) {
        this._user = val;
        this.saveLocalUser();
    }
    get online() {
        return this._online;
    }
    set online(value) {
        this._online = value;
    }
    get version() {
        return this._version;
    }
    get loggedIn() {
        return this.user ? true : false;
    }
    get isAdmin() {
        return this.user?.roles.includes("admin") ? true : false;
    }
    get logs() {
        return this._logs.reverse();
    }
    get source() {
        const scripts = document.getElementsByTagName("script");
        const lastScript = scripts[0];
        var scriptName = lastScript.src;
        return scriptName;
    }
    saveLocalToken() {
        localStorage.setItem(Storage.Token, JSON.stringify(this._token));
    }
    saveLocalUser() {
        localStorage.setItem(Storage.User, JSON.stringify(this._user));
    }
}
//# sourceMappingURL=datastore.service.js.map